import { restApi } from '../../../api/restApiSlice';

export type CDNCheckResultData = {
  result: boolean;
  cdnProvider: string | null;
  softBlocksDetected: string[];
  warnings: string[];
  botManagementSignals: string[];
  aiSpecificBlock: boolean;
  googlebotResult?: 'passed' | 'misconfigured' | 'blocked';
};

export type WAFCheckResultData = {
  result: boolean;
  hardBlocks: string[];
};

// A check whose crawler request could not complete (e.g. network error) — distinct
// from a check that ran and found a block. The backend serializes this shape per
// agent/check inside `userAgents`; the frontend treats it as "failed to run".
export type DiagnosticCheckFailure = {
  result: 'error';
  message: string;
};

// robots.txt reached the site but could not establish what the rules allow. `result` is
// absent in that case — JSON drops the undefined — so `unverifiedReason` is what marks
// it, and it selects the remediation copy. Reports written before this shape existed
// carry a bare boolean here instead, which the readers below still accept.
export type RobotsTxtUnverifiedReason =
  | 'robots-server-error'
  | 'robots-rate-limited'
  | 'robots-forbidden'
  | 'robots-unreadable';

export type RobotsTxtCheckResultData = {
  result?: boolean;
  unverifiedReason?: RobotsTxtUnverifiedReason;
};

export type CheckResult =
  | boolean
  | CDNCheckResultData
  | WAFCheckResultData
  | RobotsTxtCheckResultData
  | DiagnosticCheckFailure;
export type UserAgentChecks = Record<string, CheckResult | undefined>;

export type DiagnosticProbe = {
  probeId: string;
  check: string;
  bot: string;
  requestUrl: string;
  finalUrl: string;
  statusCode: number | null;
  responseHeaders: Record<string, string>;
  bodySnippet?: string;
  durationMs: number;
  error?: string;
  at: string;
};

export type DiagnosticsReportResponse = {
  id: string;
  url: string;
  userAgents: Record<string, UserAgentChecks>;
  // Optional: a report fetched from a rest-api that predates probes (e.g. during
  // a rollout) won't include this field. Treat a missing value as no probes.
  probes?: DiagnosticProbe[];
  createdAt: string;
};

export type UserAgentResultStatus = 'passed' | 'failed' | 'error';

export type UserAgentResult = {
  shortName: string;
  result: UserAgentResultStatus;
};

export type DiagnosticsReportListItem = {
  id: string;
  url: string;
  userAgentResults: UserAgentResult[];
  createdAt: string;
  status: 'completed' | 'partial' | 'error';
};

export type DiagnosticsReportPageInfo = {
  currentPage: number;
  totalPages: number;
  totalElements: number;
};

export type DiagnosticsReportPage = {
  items: DiagnosticsReportListItem[];
  pageInfo: DiagnosticsReportPageInfo;
};

export type DiagnosticsReportPageInput = {
  page?: number;
  pageSize?: number;
  sortBy?: string;
  sortDirection?: 'asc' | 'desc';
  domains?: string[];
};

const diagnosticsReportApi = restApi.injectEndpoints({
  endpoints: (builder) => ({
    getDiagnosticsReport: builder.query<DiagnosticsReportResponse, string>({
      query: (reportId) => ({ url: `/v3/ai-insights/diagnostics/${reportId}`, method: 'GET' }),
    }),
    getDiagnosticsReports: builder.query<DiagnosticsReportPage, DiagnosticsReportPageInput>({
      query: ({ domains, ...rest }) => ({
        url: '/v3/ai-insights/diagnostics',
        method: 'GET',
        params: {
          ...rest,
          // Wire format is a single CSV `domain` param (?domain=a.com,b.com),
          // matching the CDN Analytics array params. An absent/empty list is
          // dropped by fetchBaseQuery via `undefined`.
          domain: domains?.length ? domains.join(',') : undefined,
        },
      }),
      providesTags: ['DiagnosticsReports'],
    }),
  }),
});

export { diagnosticsReportApi };
export const {
  useGetDiagnosticsReportQuery,
  useGetDiagnosticsReportsQuery,
} = diagnosticsReportApi;
